import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { LoaderComponent } from '@components/app-loader/app-loader.component';
import { AppRoutes } from '@helpers/app-routes';
import { HttpError } from '@models/http-error.model';
import { AuthService } from '@services/auth.service';
import { ConnectionStatusService } from '@services/connection-status.service';

type RequestStatus = 'idle' | 'submitting' | 'sent';

const NOT_FOUND_STATUS = 404;
const USER_NOT_FOUND_MESSAGE = 'errors.user_not_found';
const TOO_MANY_REQUESTS_STATUS = 429;
const SECONDS_PER_MINUTE = 60;

@Component({
  selector: 'app-reset-password-request-page',
  imports: [ReactiveFormsModule, RouterLink, TranslateModule, LoaderComponent],
  templateUrl: './reset-password-request-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class ResetPasswordRequestPageComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly connectionStatus = inject(ConnectionStatusService);
  private readonly router = inject(Router);

  protected readonly loginRoute = `/${AppRoutes.login}`;

  protected readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });
  protected readonly status = signal<RequestStatus>('idle');

  submit(): void {
    if (this.status() === 'submitting') {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.status.set('submitting');

    this.authService.requestPasswordReset(this.form.getRawValue().email).subscribe({
      next: () => this.status.set('sent'),
      error: (err: HttpError) => this.#handleError(err),
    });
  }

  retry(): void {
    this.status.set('idle');
  }

  #handleError(err: HttpError): void {
    if (err.status === NOT_FOUND_STATUS && err.message === USER_NOT_FOUND_MESSAGE) {
      this.status.set('sent');
      return;
    }

    this.status.set('idle');

    if (this.connectionStatus.status() === 'common.offline') {
      this.toastr.error(
        this.translate.instant('common.offline_unavailable.message'),
        this.translate.instant('common.offline_unavailable.title'),
      );
      this.router.navigateByUrl(this.loginRoute);
      return;
    }

    if (err.status === TOO_MANY_REQUESTS_STATUS) {
      this.toastr.error(
        this.translate.instant('reset_password.request.errors.already_requested', {
          minutes: this.#getRetryAfterMinutes(err),
        }),
        this.translate.instant('reset_password.request.errors.title'),
      );
      return;
    }

    this.toastr.error(
      this.translate.instant('reset_password.request.errors.generic'),
      this.translate.instant('reset_password.request.errors.title'),
    );
  }

  #getRetryAfterMinutes(err: HttpError): number {
    const retryAfter = (err.error as { params?: { retry_after?: number } })?.params?.retry_after;

    return Math.max(1, Math.ceil((retryAfter ?? 0) / SECONDS_PER_MINUTE));
  }
}
